import * as React from "react"
import type { FormSpec } from "./formSpec"
import { getInitialValues } from "./formValues"

type HardResetOptions = {
    withNewKey: boolean
}

type FormState = {
    values: Record<string, unknown>
    setValues: React.Dispatch<React.SetStateAction<Record<string, unknown>>>
    errors: Record<string, { message: string }>
    setErrors: React.Dispatch<React.SetStateAction<Record<string, { message: string }>>>
    errorGlobal: string
    setErrorGlobal: React.Dispatch<React.SetStateAction<string>>
    loading: boolean
    setLoading: React.Dispatch<React.SetStateAction<boolean>>
    formKey: number
    bumpFormKey: () => void
    didSubmitRef: React.MutableRefObject<boolean>
    hardReset: (options: HardResetOptions) => Record<string, unknown>
}

export function useFormState(spec: FormSpec): FormState {
    const initialValues = React.useMemo(() => getInitialValues(spec), [spec])

    const [loading, setLoading] = React.useState(false)
    const [errorGlobal, setErrorGlobal] = React.useState("")
    const [values, setValues] = React.useState<Record<string, unknown>>(initialValues)
    const [errors, setErrors] = React.useState<Record<string, { message: string }>>({})
    const [formKey, setFormKey] = React.useState(0)
    const didSubmitRef = React.useRef(false)

    const bumpFormKey = React.useCallback(() => setFormKey((x) => x + 1), [])

    const hardReset = React.useCallback(
        (options: HardResetOptions): Record<string, unknown> => {
            const nextValues = getInitialValues(spec)
            didSubmitRef.current = false
            setValues(nextValues)
            setErrorGlobal("")
            setErrors({})
            if (options.withNewKey) bumpFormKey()
            return nextValues
        },
        [spec, bumpFormKey]
    )

    return {
        values,
        setValues,
        errors,
        setErrors,
        errorGlobal,
        setErrorGlobal,
        loading,
        setLoading,
        formKey,
        bumpFormKey,
        didSubmitRef,
        hardReset,
    }
}